import React, { useEffect, useState } from "react";
import apiClient from "../../config/axiosConfig";
import i18n from "../../i18n";
import StudioCLogo from "../../assets/images/StudioC-logo.png";
import { HTTP_STATUS_CODE } from "../../utilities/constants";
import MiUiSnackbar from "../../components/MiUiSnackbar/MiUiSnackbar";
import { MiUiNormalInput, MiUiPasswordInput } from "./common/MiUiInput";

const formInitialValue = {
  userName: "",
  domain: "",
  password: "",
};

export default function StudioCIntegration() {
  const [initial, setInitial] = useState(true); // To first time load and disable initial button
  const [isLoading, setIsLoading] = useState(false);
  const [initialResponse, setInitialResponse] = useState(formInitialValue); // In case of cancel preserve the previous values
  const [isEdit, setIsEdit] = useState(false);
  const [isFormValidated, setIsFormValidated] = useState(false);
  const [formValues, setFormValues] = useState(formInitialValue);
  const [snackBarConfig, setSnackBarConfig] = useState({});
  const [btnEditClick, setBtnClick] = useState(false);
  const sessionId = localStorage.getItem("SessionId");
  const token = window.genesisToken();

  const handleBlur = (event) => {
    let field = event.target.name;
    const value = event.target.value;
    if (field === "newUserName") field = "userName";
    if (field === "newPassword") field = "password";

    setFormValues({
      ...formValues,
      [field]: value,
    });
    if (initial && value) setInitial(false);
  };

  const handleSave = () => {
    setIsFormValidated(true);
    if (formValues.userName && formValues.domain && formValues.password) {
      saveStudioCConfig();
    }
  };

  const handleCancel = () => {
    let initialRes = { ...initialResponse };
    if (initialRes.userName) {
      // set the mask password
      initialRes = { ...initialRes, password: "dummy-password" };
    }
    setFormValues(initialRes);
    setInitial(true);
    setIsFormValidated(false);
    setBtnClick(false);
  };

  const handleEdit = () => {
    setFormValues(initialResponse);
    setInitial(true);
    setIsFormValidated(false);
    setBtnClick(true);
  };

  const handleFetchResponse = (resData) => {
    delete resData?.TenantType;
    setFormValues({ ...resData, password: "dummy-password" });
    setIsEdit(true);
    setInitialResponse(resData);
  };

  const handleFetchError = (error) => {
    // Check if it's a 404 error
    if (error.response && error.response.status === 404) {
      console.log("404 Not Found:", error);
    } else {
      console.error("fetchStudioCConfig -> error:", error);
    }
    // Set initial form values and handle button click
    setBtnClick(true);
    setFormValues(formInitialValue);
  };

  const fetchStudioCConfig = async () => {
    try {
      const response = await apiClient.get(`/v5/studio-c/configuration`);

      if (response.data) {
        handleFetchResponse(response.data);
      } else {
        // Set Initial Form value if no data found
        setFormValues(formInitialValue);
      }
    } catch (error) {
      handleFetchError(error);
    }
  };

  const saveStudioCConfig = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.put(
        `/v5/studio-c/configuration`,
        formValues
      );

      if (response.status === HTTP_STATUS_CODE.SUCCESS) {
        if (response.data) {
          handleFetchResponse(response.data);
          setInitial(true);
          setIsFormValidated(false);
          setBtnClick(false);
        }
        setSnackBarConfig({
          open: true,
          message: i18n.t("integrations.studioC.message.success"),
          severity: "success",
        });
      }
    } catch (e) {
      const error = JSON.stringify(e);
      setBtnClick(true);
      if (error.includes(HTTP_STATUS_CODE.FORBIDDEN)) {
        setSnackBarConfig({
          open: true,
          message: i18n.t("integrations.studioC.message.wrongCredentials"),
          severity: "error",
        });
      } else {
        setSnackBarConfig({
          open: true,
          message: i18n.t("integrations.studioC.message.serverError"),
          severity: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudioCConfig();
  }, []);

  const toggleSnackbar = () => {
    setSnackBarConfig({});
  };

  const actionButtonDisabled = isLoading || initial;

  const OnEditBtn = btnEditClick && (
    <div className="update-action">
      {isEdit && (
        <button
          className={`btn-design-system secondary`}
          onClick={handleCancel}
          type="button"
        >
          Cancel
        </button>
      )}
      <button
        className={`btn-design-system primary ${
          actionButtonDisabled ? "disabled" : ""
        }`}
        onClick={handleSave}
        type="button"
        disabled={actionButtonDisabled}
      >
        {isLoading ? "Saving..." : "Save"}
      </button>
    </div>
  );

  const AfterEditBtn = !btnEditClick && (
    <div className="update-action">
      {isEdit && (
        <button
          className={`btn-design-system primary`}
          onClick={handleEdit}
          type="button"
        >
          Edit
        </button>
      )}
    </div>
  );

  const renderInputField = (
    labelText,
    value,
    name,
    type = "text",
    disabled = false,
    hasError,
    errorMessage = "",
    withIcon = false
  ) => (
    <div className="input-element">
      {type === "password" ? (
        <MiUiPasswordInput
          label={labelText}
          value={value}
          onChange={handleBlur}
          name={name}
          disabled={disabled}
          error={hasError}
        />
      ) : (
        <MiUiNormalInput
          label={labelText}
          value={value}
          onChange={handleBlur}
          name={name}
          disabled={disabled}
          error={hasError}
        />
      )}
    </div>
  );

  return (
    <div className="studioC-wrapper">
      <MiUiSnackbar
        toggleSnackbar={toggleSnackbar}
        open={snackBarConfig.open}
        message={snackBarConfig.message}
        severity={snackBarConfig.severity}
      />
      <div className="studioC-logo">
        <img src={StudioCLogo} alt="StudioC" />
      </div>
      <div className="title-container">
        <div className="title">{i18n.t("integrations.studioC.title")}</div>
        <div className="sub-title">
          <p>{i18n.t("integrations.studioC.subtitle")}</p>
        </div>

        <div className="studioC-form">
          {renderInputField(
            i18n.t("integrations.studioC.fromElement.subDomain"),
            formValues.domain,
            "domain",
            "text",
            !btnEditClick,
            isFormValidated && !formValues.domain
          )}

          {renderInputField(
            i18n.t("integrations.studioC.fromElement.username"),
            formValues.userName,
            "newUserName",
            "text",
            !btnEditClick,
            isFormValidated && !formValues.userName
          )}

          {renderInputField(
            i18n.t("integrations.studioC.fromElement.password"),
            formValues.password,
            "newPassword",
            "password",
            !btnEditClick,
            isFormValidated && !formValues.password,
            "",
            true
          )}
          <div className="action-container">
            {OnEditBtn}
            {AfterEditBtn}
          </div>
        </div>
      </div>
    </div>
  );
}
